import { Box, Chip, Stack, Typography } from "@mui/material";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useApiQuery } from "../../hooks/use-api-query.ts";
import { STATUS_COLORS } from "../../theme/theme";
import type { CheckScheduleRun } from "../../types.ts";
import { DataGrid } from "../data-grid";
import { RunStatusChip } from "../run-status-chip.tsx";
import CheckScheduleRunDetailsDrawer from "./check-schedule-run-details-drawer.tsx";

interface CheckScheduleRunsListResponse {
  runs: CheckScheduleRun[];
  total: number;
}

interface CheckScheduleRunsTableProps {
  scheduleId: string;
  runId?: string;
  checkDependencyGraph?: Record<string, string[]> | null;
  onRunSelect?: (runId: string) => void;
  onCloseRunDrawer?: () => void;
}

export default function CheckScheduleRunsTable({
  scheduleId,
  runId,
  checkDependencyGraph,
  onRunSelect,
  onCloseRunDrawer,
}: CheckScheduleRunsTableProps) {
  const [selectedRun, setSelectedRun] = useState<CheckScheduleRun | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", paginationModel.pageSize.toString());
    params.set("offset", (paginationModel.page * paginationModel.pageSize).toString());
    return `/check-schedules/${scheduleId}/runs?${params.toString()}`;
  }, [scheduleId, paginationModel]);

  const { data: runsResponse, isLoading: isLoadingRuns } =
    useApiQuery<CheckScheduleRunsListResponse>(queryString, {
      enabled: !!scheduleId,
      refetchInterval: 5000,
    });

  const runs = runsResponse?.runs ?? [];
  const totalRows = runsResponse?.total ?? 0;

  // Fetch specific run if runId is provided
  const { data: specificRun } = useApiQuery<CheckScheduleRun>(
    `/check-schedule-runs/${runId ?? ""}`,
    {
      enabled: !!runId,
    },
  );

  // Auto-open drawer when runId is provided
  useEffect(() => {
    if (runId && specificRun) {
      setSelectedRun(specificRun);
    }
  }, [runId, specificRun]);

  const handleRowClick = (params: GridRowParams<CheckScheduleRun>) => {
    setSelectedRun(params.row);
    onRunSelect?.(params.row.id);
  };

  const handleCloseDrawer = () => {
    setSelectedRun(null);
    onCloseRunDrawer?.();
  };

  const columns: GridColDef<CheckScheduleRun>[] = [
    {
      field: "run_status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => <RunStatusChip status={params.value} />,
    },
    {
      field: "total_checks",
      headerName: "Checks",
      width: 200,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${row.passed_checks}/${row.total_checks}`}
              size="small"
              sx={{
                fontSize: "0.75rem",
                backgroundColor:
                  row.passed_checks === row.total_checks
                    ? STATUS_COLORS.success.bg
                    : row.failed_checks > 0
                      ? STATUS_COLORS.error.bg
                      : "grey.100",
                color:
                  row.passed_checks === row.total_checks
                    ? STATUS_COLORS.success.text
                    : row.failed_checks > 0
                      ? STATUS_COLORS.error.text
                      : "grey.600",
              }}
            />
            {row.failed_checks > 0 && (
              <Typography variant="caption" color="error">
                {row.failed_checks} failed
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: "duration_seconds",
      headerName: "Duration",
      width: 120,
      valueFormatter: (value: number | null) => {
        if (value === null || value === undefined) return "-";
        if (value < 60) return `${value}s`;
        const mins = Math.floor(value / 60);
        const secs = value % 60;
        return `${mins}m ${secs}s`;
      },
    },
    {
      field: "trigger",
      headerName: "Trigger",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || "manual"}
          size="small"
          sx={{
            fontSize: "0.7rem",
            height: 20,
            backgroundColor: params.value === "cron" ? STATUS_COLORS.info.bg : "grey.100",
            color: params.value === "cron" ? STATUS_COLORS.info.text : "grey.700",
          }}
        />
      ),
    },
    {
      field: "start_time",
      type: "dateTime",
      headerName: "Started At",
      width: 180,
      valueGetter: (val: CheckScheduleRun["start_time"]) => (val ? new Date(val) : null),
    },
    {
      field: "end_time",
      type: "dateTime",
      headerName: "Ended At",
      width: 180,
      valueGetter: (val: CheckScheduleRun["end_time"]) => (val ? new Date(val) : null),
    },
    {
      field: "created_at",
      type: "dateTime",
      headerName: "Created At",
      width: 180,
      valueGetter: (val: CheckScheduleRun["created_at"]) => new Date(val),
    },
  ];

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
      }}
    >
      <DataGrid
        loading={isLoadingRuns}
        rows={runs}
        columns={columns}
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        minHeight="calc(100vh - 300px)"
        onRowClick={handleRowClick}
      />

      {/* Schedule Run Details Drawer */}
      {selectedRun && (
        <CheckScheduleRunDetailsDrawer
          open={!!selectedRun}
          onClose={handleCloseDrawer}
          scheduleRun={selectedRun}
          scheduleId={scheduleId}
          checkDependencyGraph={checkDependencyGraph}
        />
      )}
    </Box>
  );
}
